import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { coversApi } from '@/api/covers';
import { COVER_TEMPLATES } from '@/components/cover/templates/registry';
import { useAuth } from '@/context/AuthContext';
import { useBrandLogo } from '@/hooks/use-brand-logo';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useReportWizard } from '../context/ReportWizardContext';
import type { ApiError } from '@/api/client';
import type { CoverFieldContext } from '@/components/cover/resolve-cover-fields';
import type {
    CoverColors,
    CoverConfig,
    CoverFieldKey,
    CoverTemplateKey,
    CoverTitleSize,
    ReportCover,
} from '@/types/cover';

export const COVER_VIEW_PARAM = 'view';
export const COVER_VIEW_VALUE = 'capa';

const DEFAULT_COVER_NAME = 'Minha capa';

/** The persistable config, with the snapshot-only provenance keys stripped. */
const toCoverConfig = (cover: ReportCover): CoverConfig => ({
    template_key: cover.template_key,
    ...(cover.show_logo !== undefined ? { show_logo: cover.show_logo } : {}),
    ...(cover.colors ? { colors: cover.colors } : {}),
    ...(cover.font ? { font: cover.font } : {}),
    ...(cover.title_size ? { title_size: cover.title_size } : {}),
    ...(cover.fields ? { fields: cover.fields } : {}),
});

/** Stable comparison: key order is fixed by construction in toCoverConfig. */
const isSameConfig = (a: CoverConfig, b: CoverConfig) =>
    JSON.stringify(toCoverConfig(a as ReportCover)) ===
    JSON.stringify(toCoverConfig(b as ReportCover));

export const useCoverViewState = () => {
    const { formData, handleUpdate, isReadOnly } = useReportWizard();
    const { user } = useAuth();
    const [, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const coverAccess = useFeatureEntitlement('reports.cover');

    /**
     * Sem a capa no plano, tudo vira prévia: a API 403 qualquer save que carregue
     * customizations.cover, o que travaria o laudo inteiro. Então o laudo nunca
     * recebe a capa — as edições ficam no draft local e morrem com a view.
     */
    const isLocked = coverAccess.shouldBlock;
    const reportCover = formData.customizations?.cover;
    const appliedCover = isLocked ? undefined : reportCover;
    const [draft, setDraft] = useState<ReportCover | null>(null);

    /** Quem baixou de plano continua vendo a própria capa, agora só como prévia. */
    const lockedSeed = isLocked ? (reportCover ?? null) : null;

    /** The cover being edited: the laudo's own snapshot, or the pre-apply draft. */
    const cover = appliedCover ?? draft ?? lockedSeed;

    const { data: covers = [] } = useQuery({
        queryKey: ['covers'],
        queryFn: coversApi.list,
        enabled: !isLocked,
    });

    const savedCover = useMemo(
        () => (cover?.cover_id ? covers.find((c) => c.id === cover.cover_id) : undefined),
        [covers, cover?.cover_id],
    );

    const isDiverged = useMemo(
        () =>
            Boolean(cover && savedCover && !isSameConfig(toCoverConfig(cover), savedCover.config)),
        [cover, savedCover],
    );

    const fieldContext: CoverFieldContext = useMemo(
        () => ({
            patient: formData.patient,
            finalidade: formData.finalidade,
            psychologist: user
                ? {
                      full_name: user.full_name,
                      crp: user.crp,
                      city: user.city,
                      state: user.state,
                  }
                : undefined,
        }),
        [formData.patient, formData.finalidade, user],
    );

    const updateCover = useCallback(
        (updater: (prev: ReportCover) => ReportCover) => {
            if (appliedCover) {
                handleUpdate((prev) => ({
                    customizations: {
                        ...prev.customizations,
                        cover: updater(prev.customizations?.cover ?? appliedCover),
                    },
                }));
                return;
            }

            setDraft((prev) => {
                const base = prev ?? lockedSeed;
                return base ? updater(base) : base;
            });
        },
        [appliedCover, handleUpdate, lockedSeed],
    );

    const selectTemplate = useCallback(
        (templateKey: CoverTemplateKey) => {
            if (cover) {
                updateCover((prev) => ({ ...prev, template_key: templateKey }));
                return;
            }

            setDraft({ template_key: templateKey });
        },
        [cover, updateCover],
    );

    const setFieldVisible = useCallback(
        (keys: CoverFieldKey[], visible: boolean) => {
            updateCover((prev) => ({
                ...prev,
                fields: {
                    ...prev.fields,
                    ...Object.fromEntries(
                        keys.map((key) => [key, { ...prev.fields?.[key], visible }]),
                    ),
                },
            }));
        },
        [updateCover],
    );

    const setSubtitle = useCallback(
        (text: string) => {
            updateCover((prev) => ({
                ...prev,
                fields: {
                    ...prev.fields,
                    subtitle: { visible: true, text },
                },
            }));
        },
        [updateCover],
    );

    const setColors = useCallback(
        (colors: CoverColors | undefined) => {
            updateCover((prev) => {
                const next = { ...prev };
                if (colors) next.colors = colors;
                else delete next.colors;
                return next;
            });
        },
        [updateCover],
    );

    /** undefined = tipografia mista nativa do template. */
    const setFont = useCallback(
        (font: string | undefined) => {
            updateCover((prev) => {
                const next = { ...prev };
                if (font) next.font = font;
                else delete next.font;
                return next;
            });
        },
        [updateCover],
    );

    const setTitleSize = useCallback(
        (titleSize: CoverTitleSize) => {
            updateCover((prev) => {
                const next = { ...prev };
                if (titleSize === 'medium') delete next.title_size;
                else next.title_size = titleSize;
                return next;
            });
        },
        [updateCover],
    );

    const closeCoverView = useCallback(() => {
        setSearchParams(
            (params) => {
                params.delete(COVER_VIEW_PARAM);
                return params;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const applyToReport = useCallback(
        (nextCover: ReportCover) => {
            handleUpdate((prev) => ({
                customizations: {
                    ...prev.customizations,
                    cover: nextCover,
                },
            }));
        },
        [handleUpdate],
    );

    /** Toggle the cover's logo visibility (per-cover; independent of the asset). */
    const setShowLogo = useCallback(
        (visible: boolean) => {
            updateCover((prev) => ({ ...prev, show_logo: visible }));
        },
        [updateCover],
    );

    /**
     * The cover's logo is the shared brand logo (customizations.header.logo_url),
     * the same asset the header/footer use — not a cover-only field. A fresh
     * upload is meant to be shown, so it also turns the cover logo on.
     */
    const brandLogo = useBrandLogo({
        data: formData,
        onUpdate: handleUpdate,
        surface: 'report_cover',
        onUploaded: () => setShowLogo(true),
    });

    const saveAsDefaultMutation = useMutation({
        mutationFn: async (target: ReportCover) => {
            const config = toCoverConfig(target);
            const baseName = savedCover?.name ?? DEFAULT_COVER_NAME;

            // A duplicate name 409s; retry with a numeric suffix for new covers.
            for (let attempt = 0; attempt < 5; attempt += 1) {
                const name = attempt === 0 ? baseName : `${baseName} ${attempt + 1}`;
                try {
                    return await coversApi.upsert({
                        id: target.cover_id,
                        name,
                        is_default: true,
                        config,
                    });
                } catch (error) {
                    const status = (error as ApiError).response?.status;
                    if (status !== 409 || target.cover_id) throw error;
                }
            }

            throw new Error('cover name conflict');
        },
        onSuccess: (saved, target) => {
            void queryClient.invalidateQueries({ queryKey: ['covers'] });
            applyToReport({
                ...toCoverConfig(target),
                cover_id: saved.id,
                applied_at: new Date().toISOString(),
            });
            toast.success('Capa salva — ela será aplicada em todos os laudos novos.');
            closeCoverView();
        },
        onError: () => {
            toast.error('Não foi possível salvar a capa. Tente novamente.');
        },
    });

    const promoteMutation = useMutation({
        mutationFn: async (target: ReportCover) => {
            if (!target.cover_id || !savedCover) throw new Error('missing cover');

            return coversApi.upsert({
                id: target.cover_id,
                name: savedCover.name,
                config: toCoverConfig(target),
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['covers'] });
            toast.success('Capa atualizada para os próximos laudos.');
        },
        onError: () => {
            toast.error('Não foi possível atualizar a capa. Tente novamente.');
        },
    });

    const useOnlyOnThisReport = useCallback(() => {
        if (isLocked || !draft) return;

        applyToReport({ ...draft, applied_at: new Date().toISOString() });
        toast.success('Capa aplicada a este laudo.');
        closeCoverView();
    }, [isLocked, draft, applyToReport, closeCoverView]);

    return {
        isReadOnly,
        isLocked,
        coverAccess,
        cover,
        /** True once the laudo itself carries the cover (edits auto-save). */
        isApplied: Boolean(appliedCover),
        savedCover,
        isDiverged,
        fieldContext,
        customizations: formData.customizations,
        patientName: formData.patient?.full_name,
        patientFirstName: formData.patient?.full_name?.split(' ')[0],
        templates: COVER_TEMPLATES,
        selectTemplate,
        setFieldVisible,
        setSubtitle,
        setColors,
        setFont,
        setTitleSize,
        // Logo (shared brand asset in customizations.header.logo_url)
        logoAccess: brandLogo.logoAccess,
        logoUrl: brandLogo.logoUrl,
        hasLogo: brandLogo.hasLogo,
        showLogo: cover?.show_logo !== false,
        logoUploadState: brandLogo.uploadState,
        uploadLogo: brandLogo.uploadLogo,
        removeLogo: brandLogo.removeLogo,
        setShowLogo,
        saveAsDefault: (target: ReportCover) => {
            if (isLocked) return;
            saveAsDefaultMutation.mutate(target);
        },
        isSavingDefault: saveAsDefaultMutation.isPending,
        promote: (target: ReportCover) => {
            if (isLocked) return;
            promoteMutation.mutate(target);
        },
        isPromoting: promoteMutation.isPending,
        useOnlyOnThisReport,
        closeCoverView,
    };
};
